import React from 'react';
import { useTranslation } from 'react-i18next';
import './TrafficChart.scss';
import { roundedTopPath } from './charts/chartUtils';

const CHART_DATA = [
  { week: "W19'25", trials: 0, traffic: 1 },
  { week: "W20'25", trials: 0, traffic: 1 },
  { week: "W21'25", trials: 0, traffic: 2 },
  { week: "W22'25", trials: 0, traffic: 1 },
  { week: "W23'25", trials: 0, traffic: 1 },
  { week: "W24'25", trials: 0, traffic: 2 },
  { week: "W25'25", trials: 0, traffic: 2 },
  { week: "W26'25", trials: 0, traffic: 1 },
  { week: "W27'25", trials: 0, traffic: 2 },
  { week: "W28'25", trials: 0, traffic: 1 },
  { week: "W29'25", trials: 0, traffic: 2 },
  { week: "W30'25", trials: 0, traffic: 1 },
  { week: "W31'25", trials: 15, traffic: 13 },
  { week: "W32'25", trials: 43, traffic: 19 },
  { week: "W33'25", trials: 72, traffic: 20 },
  { week: "W34'25", trials: 84, traffic: 22 },
  { week: "W35'25", trials: 80, traffic: 21 },
  { week: "W36'25", trials: 102, traffic: 34 },
  { week: "W37'25", trials: 107, traffic: 30 },
  { week: "W38'25", trials: 139, traffic: 28 },
  { week: "W39'25", trials: 138, traffic: 37 },
  { week: "W40'25", trials: 144, traffic: 35 },
  { week: "W41'25", trials: 164, traffic: 30 },
  { week: "W42'25", trials: 163, traffic: 22 },
  { week: "W43'25", trials: 117, traffic: 26 },
  { week: "W44'25", trials: 171, traffic: 39 },
  { week: "W45'25", trials: 224, traffic: 39 },
  { week: "W46'25", trials: 291, traffic: 61 },
];

const MAX_Y = 370;
const Y_TICKS = [0, 100, 200, 300];
const PADDING = { top: 24, right: 15, bottom: 65, left: 55 };
const VB_W = 800;
const VB_H = 380;
const CHART_W = VB_W - PADDING.left - PADDING.right;
const CHART_H = VB_H - PADDING.top - PADDING.bottom;
const BAR_SLOT = CHART_W / CHART_DATA.length;
const OUTER_GAP = 2;
const DEPTH_OFFSET = 5;
const BAR_W = BAR_SLOT - OUTER_GAP - DEPTH_OFFSET;
const CORNER_R = 2;
const SCALE_Y = CHART_H / MAX_Y;
const CHART_BOTTOM = PADDING.top + CHART_H;

const TrafficChart: React.FC = () => {
  const { t } = useTranslation();

  return (
  <div className="traffic-chart">
    <svg
      className="traffic-chart__svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <filter id="barShadow" x="-20%" y="-10%" width="150%" height="130%">
          <feDropShadow dx="1.5" dy="1" stdDeviation="1.5" floodColor="#000" floodOpacity="0.18" />
        </filter>
        <filter id="backBarShadow" x="-20%" y="-10%" width="150%" height="130%">
          <feDropShadow dx="1" dy="0.5" stdDeviation="1" floodColor="#000" floodOpacity="0.10" />
        </filter>
      </defs>

      {Y_TICKS.map((tick) => {
        const y = CHART_BOTTOM - tick * SCALE_Y;
        return (
          <line
            key={`grid-${tick}`}
            className="traffic-chart__grid-line"
            x1={PADDING.left}
            y1={y}
            x2={VB_W - PADDING.right}
            y2={y}
          />
        );
      })}

      <text
        className="traffic-chart__y-title"
        x={8}
        y={PADDING.top + CHART_H / 2}
        textAnchor="middle"
        transform={`rotate(-90, 8, ${PADDING.top + CHART_H / 2})`}
      >
        {t('geoChecklist.chart.yAxis')}
      </text>

      {Y_TICKS.map((tick) => {
        const y = CHART_BOTTOM - tick * SCALE_Y;
        return (
          <text
            key={`label-${tick}`}
            className="traffic-chart__y-label"
            x={PADDING.left - 6}
            y={y + 3.5}
            textAnchor="end"
          >
            {tick}
          </text>
        );
      })}

      {CHART_DATA.map((d, i) => {
        const slotX = PADDING.left + i * BAR_SLOT + OUTER_GAP / 2;
        const backX = slotX + DEPTH_OFFSET;
        const frontX = slotX;
        const trialsH = d.trials * SCALE_Y;
        const trafficH = d.traffic * SCALE_Y;

        return (
          <g key={d.week}>
            {trialsH > 0 && (
              <path
                className="traffic-chart__bar--trials"
                filter="url(#backBarShadow)"
                d={roundedTopPath(backX, CHART_BOTTOM - trialsH, BAR_W, trialsH, CORNER_R)}
              />
            )}
            {trafficH > 0 && (
              <path
                className="traffic-chart__bar--traffic"
                filter="url(#barShadow)"
                d={roundedTopPath(frontX, CHART_BOTTOM - trafficH, BAR_W, trafficH, CORNER_R)}
              />
            )}
            {trialsH > 0 && (() => {
              const cx = backX + BAR_W / 2;
              const cy = CHART_BOTTOM - trialsH - 10;
              const pw = String(d.trials).length * 5 + 8;
              return (
                <g>
                  <rect
                    className="traffic-chart__badge traffic-chart__badge--trials"
                    x={cx - pw / 2} y={cy - 6}
                    width={pw} height={12} rx={4}
                  />
                  <text className="traffic-chart__badge-text" x={cx} y={cy + 3} textAnchor="middle">
                    {d.trials}
                  </text>
                </g>
              );
            })()}
            {trafficH > 0 && (() => {
              const cx = frontX + BAR_W / 2;
              const cy = CHART_BOTTOM - trafficH - 10;
              const pw = String(d.traffic).length * 5 + 8;
              return (
                <g>
                  <rect
                    className="traffic-chart__badge traffic-chart__badge--traffic"
                    x={cx - pw / 2} y={cy - 6}
                    width={pw} height={12} rx={4}
                  />
                  <text className="traffic-chart__badge-text" x={cx} y={cy + 3} textAnchor="middle">
                    {d.traffic}
                  </text>
                </g>
              );
            })()}
          </g>
        );
      })}

      {CHART_DATA.map((d, i) => {
        const slotCenter = PADDING.left + i * BAR_SLOT + BAR_SLOT / 2;
        const labelY = CHART_BOTTOM + 12;
        return (
          <text
            key={d.week}
            className="traffic-chart__x-label"
            x={slotCenter}
            y={labelY}
            textAnchor="end"
            transform={`rotate(-45, ${slotCenter}, ${labelY})`}
          >
            {d.week}
          </text>
        );
      })}
    </svg>

    <div className="traffic-chart__legend">
      <span className="traffic-chart__legend-item">
        <span className="traffic-chart__legend-dot traffic-chart__legend-dot--trials" />
        {t('geoChecklist.chart.trials')}
      </span>
      <span className="traffic-chart__legend-item">
        <span className="traffic-chart__legend-dot traffic-chart__legend-dot--traffic" />
        {t('geoChecklist.chart.traffic')}
      </span>
    </div>
  </div>
  );
};

export { TrafficChart };
export default TrafficChart;
